import { ACTOR_ID_HEADER_KEY, PAYMENT_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const BASE = `${PAYMENT_SERVICE_BASE_URL}/api/v1/workspace-payrolls`;

const actorHeaders = (actorId) =>
  actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {};

const initiatePayrollPayoutRequest = async ({ actorId, workspaceId, paymentId, payoutDetails } = {}) => {
  const response = await apiClient.post(
    `${BASE}/workspaces/${encodeURIComponent(workspaceId)}/payments/${encodeURIComponent(paymentId)}/payout`,
    {
      ...(payoutDetails || {}),
      payrollPaymentDetailsId: payoutDetails?.payrollPaymentDetailsId || paymentId,
    },
    {
      headers: actorHeaders(actorId),
      params: { wid: workspaceId },
    },
  );
  return response.data || null;
};

export const workspacePayrollService = {
  listItems: async ({ actorId, workspaceId, kind } = {}) => {
    const response = await apiClient.get(`${BASE}/workspaces/${encodeURIComponent(workspaceId)}/items`, {
      headers: actorHeaders(actorId),
      params: {
        kind,
        wid: workspaceId,
      },
    });
    return response.data || [];
  },

  createItem: async ({ actorId, workspaceId, item } = {}) => {
    const response = await apiClient.post(
      `${BASE}/workspaces/${encodeURIComponent(workspaceId)}/items`,
      item,
      {
        headers: actorHeaders(actorId),
        params: { wid: workspaceId },
      },
    );
    return response.data || null;
  },

  listSchedules: async ({ actorId, workspaceId } = {}) => {
    const response = await apiClient.get(`${BASE}/workspaces/${encodeURIComponent(workspaceId)}/schedules`, {
      headers: actorHeaders(actorId),
      params: {
        wid: workspaceId,
      },
    });
    return response.data || [];
  },

  createSchedule: async ({ actorId, workspaceId, schedule } = {}) => {
    const response = await apiClient.post(
      `${BASE}/workspaces/${encodeURIComponent(workspaceId)}/schedules`,
      schedule,
      {
        headers: actorHeaders(actorId),
        params: { wid: workspaceId },
      },
    );
    return response.data || null;
  },

  listWashingtonLiConfigs: async ({ actorId, workspaceId } = {}) => {
    const response = await apiClient.get(`${BASE}/workspaces/${encodeURIComponent(workspaceId)}/washington-lni-configs`, {
      headers: actorHeaders(actorId),
      params: {
        wid: workspaceId,
      },
    });
    return response.data || [];
  },

  createWashingtonLiConfig: async ({ actorId, workspaceId, config } = {}) => {
    const response = await apiClient.post(
      `${BASE}/workspaces/${encodeURIComponent(workspaceId)}/washington-lni-configs`,
      config,
      {
        headers: actorHeaders(actorId),
        params: { wid: workspaceId },
      },
    );
    return response.data || null;
  },

  computeFederalIncomeTax: async ({ actorId, workspaceId, payload } = {}) => {
    const response = await apiClient.post(
      `${BASE}/workspaces/${encodeURIComponent(workspaceId)}/federal-income-tax/compute`,
      payload,
      {
        headers: actorHeaders(actorId),
        params: { wid: workspaceId },
      },
    );
    return response.data || null;
  },

  computeWashingtonPayrollTaxes: async ({ actorId, workspaceId, payload } = {}) => {
    const response = await apiClient.post(
      `${BASE}/workspaces/${encodeURIComponent(workspaceId)}/washington-payroll-taxes/compute`,
      payload,
      {
        headers: actorHeaders(actorId),
        params: { wid: workspaceId },
      },
    );
    return response.data || null;
  },

  listPayments: async ({ actorId, workspaceId, userId } = {}) => {
    const response = await apiClient.get(`${BASE}/workspaces/${encodeURIComponent(workspaceId)}/payments`, {
      headers: actorHeaders(actorId),
      params: {
        ...(userId ? { userId } : {}),
        wid: workspaceId,
      },
    });
    return response.data || [];
  },

  getPayment: async ({ actorId, workspaceId, paymentId } = {}) => {
    const response = await apiClient.get(
      `${BASE}/workspaces/${encodeURIComponent(workspaceId)}/payments/${encodeURIComponent(paymentId)}`,
      {
        headers: actorHeaders(actorId),
        params: { wid: workspaceId },
      },
    );
    return response.data || null;
  },

  createManualPayment: async ({ actorId, workspaceId, paymentDetails } = {}) => {
    const response = await apiClient.post(
      `${BASE}/workspaces/${encodeURIComponent(workspaceId)}/payments/manual`,
      paymentDetails || {},
      {
        headers: actorHeaders(actorId),
        params: { wid: workspaceId },
      },
    );
    return response.data || null;
  },

  initiatePayment: async ({ actorId, workspaceId, paymentDetails } = {}) => {
    const response = await apiClient.post(
      `${BASE}/workspaces/${encodeURIComponent(workspaceId)}/payments/pay-now`,
      paymentDetails,
      {
        headers: actorHeaders(actorId),
        params: { wid: workspaceId },
      },
    );
    return response.data || null;
  },

  retryPaymentCharge: async ({ actorId, workspaceId, paymentId } = {}) => {
    const response = await apiClient.post(
      `${BASE}/workspaces/${encodeURIComponent(workspaceId)}/payments/${encodeURIComponent(paymentId)}/retry-charge`,
      {},
      {
        headers: actorHeaders(actorId),
        params: { wid: workspaceId },
      },
    );
    return response.data || null;
  },

  initiatePayrollPayout: initiatePayrollPayoutRequest,

  initiatePayout: initiatePayrollPayoutRequest,

  listTaxRemittances: async ({ actorId, workspaceId, taxCode } = {}) => {
    const response = await apiClient.get(`${BASE}/workspaces/${encodeURIComponent(workspaceId)}/tax-remittances`, {
      headers: actorHeaders(actorId),
      params: {
        ...(taxCode ? { taxCode } : {}),
        wid: workspaceId,
      },
    });
    return response.data || [];
  },

  listTaxRemittanceSummary: async ({ actorId, workspaceId } = {}) => {
    const response = await apiClient.get(`${BASE}/workspaces/${encodeURIComponent(workspaceId)}/tax-remittances/summary`, {
      headers: actorHeaders(actorId),
      params: {
        wid: workspaceId,
      },
    });
    return response.data || [];
  },

  createTaxRemittance: async ({ actorId, workspaceId, remittance } = {}) => {
    const response = await apiClient.post(
      `${BASE}/workspaces/${encodeURIComponent(workspaceId)}/tax-remittances`,
      remittance,
      {
        headers: actorHeaders(actorId),
        params: { wid: workspaceId },
      },
    );
    return response.data || null;
  },
};

export default workspacePayrollService;
